import React, { useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Collapse,
  Drawer,
  Popover,
  Radio,
  Select,
  Slider,
  Space,
  Switch,
} from "antd";
import { FilterOutlined } from "@ant-design/icons";
import "./FlightResultsFiltersPanel.css";

export const SORT_OPTIONS = [
  { value: "cheapest", label: "הזול ביותר" },
  { value: "fastest", label: "המהיר ביותר" },
  { value: "recommended", label: "המומלץ ביותר" },
  { value: "earliest_out", label: "שעת יציאה מוקדמת" },
  { value: "latest_out", label: "שעת יציאה מאוחרת" },
  { value: "fewest_stops", label: "פחות עצירות" },
];

const TIME_OPTIONS = [
  { value: "morning", label: "בוקר (05–12)" },
  { value: "afternoon", label: "צהריים (12–17)" },
  { value: "evening", label: "ערב (17–21)" },
  { value: "night", label: "לילה (21–05)" },
];

const AIRLINE_NAME_FALLBACK_HE = {
  "6H": "ישראייר",
  BZ: "בלו בירד",
  IZ: "ארקיע",
  LY: "אל על",
};

function formatMinutes(m) {
  const n = Math.floor(Number(m));
  if (!Number.isFinite(n) || n < 0) return "—";
  const h = Math.floor(n / 60);
  const mm = n % 60;
  if (h <= 0) return `${mm} דק׳`;
  return `${h} שע׳ ${mm > 0 ? `${mm} דק׳` : ""}`.trim();
}

function FiltersFormBody({
  filter,
  setFilter,
  bounds,
  routeCounts,
  showRouteFilter,
  airlineOptions,
  airlineNameByCode,
  depAirportOptions,
  arrAirportOptions,
  hasReturnSegment,
  compactGrid = false,
}) {
  const ppBounds = bounds?.pricePerPerson;
  const tfBounds = bounds?.totalFare;
  const durBounds = bounds?.durationMin;

  const useTotal = filter.useTotalPriceForRange;
  const activePriceBounds = useTotal ? tfBounds : ppBounds;

  const priceSliderValue = useMemo(() => {
    if (!activePriceBounds) return null;
    const [lo, hi] = activePriceBounds;
    const r = filter.useTotalPriceForRange
      ? filter.totalFareRange
      : filter.pricePerPersonRange;
    if (r && r.length === 2) return r;
    return [lo, hi];
  }, [
    activePriceBounds,
    filter.pricePerPersonRange,
    filter.totalFareRange,
    filter.useTotalPriceForRange,
  ]);

  const durSliderValue = useMemo(() => {
    if (!durBounds) return null;
    const [lo, hi] = durBounds;
    if (filter.durationRangeMin && filter.durationRangeMin.length === 2) {
      return filter.durationRangeMin;
    }
    return [lo, hi];
  }, [durBounds, filter.durationRangeMin]);

  const sections = (
    <>
      {showRouteFilter ? (
        <div className="fr-filters-section">
          <h3 className="fr-filters-section__title">סוג מסלול</h3>
          <Radio.Group
            value={filter.routeType}
            onChange={(e) => setFilter({ routeType: e.target.value })}
          >
            <Space direction="vertical">
              <Radio value="all">
                הכל ({routeCounts.total})
              </Radio>
              {routeCounts.rt > 0 ? (
                <Radio value="rt">
                  הלוך-חזור (RT) ({routeCounts.rt})
                </Radio>
              ) : null}
              {routeCounts.owOut > 0 ? (
                <Radio value="ow_out">
                  הלוך בלבד (OW) ({routeCounts.owOut})
                </Radio>
              ) : null}
              {routeCounts.owIn > 0 ? (
                <Radio value="ow_in">
                  חזור בלבד (OW) ({routeCounts.owIn})
                </Radio>
              ) : null}
            </Space>
          </Radio.Group>
        </div>
      ) : null}

      <div className="fr-filters-section">
        <h3 className="fr-filters-section__title">מחיר</h3>
        <div style={{ marginBottom: 8 }}>
          <Space align="center" wrap>
            <span style={{ fontSize: 13 }}>בסיס מחיר:</span>
            <Switch
              checked={useTotal}
              onChange={(checked) =>
                setFilter({
                  useTotalPriceForRange: checked,
                  pricePerPersonRange: null,
                  totalFareRange: null,
                })
              }
              checkedChildren="סה״כ"
              unCheckedChildren="לאדם"
            />
          </Space>
        </div>
        {activePriceBounds && priceSliderValue ? (
          <div className="fr-filters-slider-row">
            <Slider
              range
              min={activePriceBounds[0]}
              max={activePriceBounds[1]}
              step={1}
              value={priceSliderValue}
              onChange={(v) => {
                if (Array.isArray(v) && v.length === 2) {
                  if (useTotal) {
                    setFilter({ totalFareRange: v });
                  } else {
                    setFilter({ pricePerPersonRange: v });
                  }
                }
              }}
              tooltip={{
                formatter: (v) => (v != null ? Math.round(v) : ""),
              }}
            />
            <p className="fr-filters-section__hint">
              גרירה לצמצום טווח; ברירת מחדל מלאה — כל המחירים
            </p>
          </div>
        ) : (
          <p className="fr-filters-section__hint">אין נתוני מחיר מספריים לסינון</p>
        )}
      </div>

      <div className="fr-filters-section">
        <h3 className="fr-filters-section__title">שעות טיסה (יציאה)</h3>
        <Space direction="vertical" style={{ width: "100%" }}>
          <div>
            <span style={{ fontSize: 12, color: "#64748b" }}>הלוך</span>
            <Select
              allowClear
              placeholder="כל השעות"
              style={{ width: "100%", marginTop: 4 }}
              value={filter.timeOutbound}
              onChange={(v) => setFilter({ timeOutbound: v ?? null })}
              options={TIME_OPTIONS}
            />
          </div>
          {hasReturnSegment ? (
            <div>
              <span style={{ fontSize: 12, color: "#64748b" }}>חזור</span>
              <Select
                allowClear
                placeholder="כל השעות"
                style={{ width: "100%", marginTop: 4 }}
                value={filter.timeReturn}
                onChange={(v) => setFilter({ timeReturn: v ?? null })}
                options={TIME_OPTIONS}
              />
            </div>
          ) : null}
        </Space>
      </div>

      <div className="fr-filters-section">
        <h3 className="fr-filters-section__title">מספר עצירות (סה״כ בטיסה)</h3>
        <Radio.Group
          value={filter.stops ?? "any"}
          onChange={(e) => {
            const v = e.target.value;
            setFilter({ stops: v === "any" ? null : v });
          }}
        >
          <Space direction="vertical">
            <Radio value="any">הכל</Radio>
            <Radio value="direct">ישירה בלבד</Radio>
            <Radio value="one">עצירה אחת</Radio>
            <Radio value="two_plus">שתי עצירות ומעלה</Radio>
          </Space>
        </Radio.Group>
      </div>

      <div className="fr-filters-section">
        <h3 className="fr-filters-section__title">חברות תעופה</h3>
        {airlineOptions.length === 0 ? (
          <p className="fr-filters-section__hint">אין רשימת חברות מזוהה</p>
        ) : (
          <Checkbox.Group
            className="fr-filters-checkboxes"
            style={{ display: "flex", flexDirection: "column" }}
            value={filter.airlines}
            onChange={(v) => setFilter({ airlines: v })}
          >
            {airlineOptions.map(({ code, count }) => (
              <Checkbox key={code} value={code}>
                {(airlineNameByCode[code] || code) +
                  (airlineNameByCode[code] ? ` (${code})` : "")}{" "}
                ({count})
              </Checkbox>
            ))}
          </Checkbox.Group>
        )}
      </div>

      <div className="fr-filters-section">
        <h3 className="fr-filters-section__title">כבודה (לפי פריטים מהספק)</h3>
        <Radio.Group
          value={filter.baggage ?? "any"}
          onChange={(e) => {
            const v = e.target.value;
            setFilter({ baggage: v === "any" ? null : v });
          }}
        >
          <Space direction="vertical">
            <Radio value="any">הכל</Radio>
            <Radio value="personal_only">תיק אישי בלבד (ללא טרולי/מזוודה בתוספת)</Radio>
            <Radio value="trolley">כולל טרולי</Radio>
            <Radio value="checked">כולל מזוודה</Radio>
            <Radio value="unknown">ללא מידע על כבודה</Radio>
          </Space>
        </Radio.Group>
      </div>

      <div className="fr-filters-section">
        <h3 className="fr-filters-section__title">משך טיסה כולל</h3>
        {durBounds && durSliderValue ? (
          <div className="fr-filters-slider-row">
            <Slider
              range
              min={durBounds[0]}
              max={durBounds[1]}
              step={5}
              value={durSliderValue}
              onChange={(v) => {
                if (Array.isArray(v) && v.length === 2) {
                  setFilter({ durationRangeMin: v });
                }
              }}
              tooltip={{
                formatter: (v) => (v != null ? formatMinutes(v) : ""),
              }}
            />
            <p className="fr-filters-section__hint">
              {formatMinutes(durSliderValue[0])} – {formatMinutes(durSliderValue[1])}
            </p>
          </div>
        ) : (
          <p className="fr-filters-section__hint">אין נתוני משך מספריים</p>
        )}
      </div>

      <div className="fr-filters-section">
        <h3 className="fr-filters-section__title">שדות יציאה</h3>
        {depAirportOptions.length === 0 ? (
          <p className="fr-filters-section__hint">אין נתונים</p>
        ) : (
          <Checkbox.Group
            className="fr-filters-checkboxes"
            style={{ display: "flex", flexDirection: "column" }}
            value={filter.depAirports}
            onChange={(v) => setFilter({ depAirports: v })}
          >
            {depAirportOptions.map(({ code, count }) => (
              <Checkbox key={code} value={code}>
                {code} ({count})
              </Checkbox>
            ))}
          </Checkbox.Group>
        )}
      </div>

      <div className="fr-filters-section">
        <h3 className="fr-filters-section__title">שדות נחיתה</h3>
        {arrAirportOptions.length === 0 ? (
          <p className="fr-filters-section__hint">אין נתונים</p>
        ) : (
          <Checkbox.Group
            className="fr-filters-checkboxes"
            style={{ display: "flex", flexDirection: "column" }}
            value={filter.arrAirports}
            onChange={(v) => setFilter({ arrAirports: v })}
          >
            {arrAirportOptions.map(({ code, count }) => (
              <Checkbox key={code} value={code}>
                {code} ({count})
              </Checkbox>
            ))}
          </Checkbox.Group>
        )}
      </div>
    </>
  );

  return (
    <div
      className={[
        "fr-filters-drawer-body",
        compactGrid ? "fr-filters-drawer-body--compact-root" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      dir="rtl"
    >
      {compactGrid ? (
        <div className="fr-filters-form-grid">{sections}</div>
      ) : (
        sections
      )}
    </div>
  );
}

function buildOptionsWithCounts(traitsList, key) {
  const m = new Map();
  for (const t of traitsList) {
    const arr = t[key] || [];
    for (const code of arr) {
      m.set(code, (m.get(code) || 0) + 1);
    }
  }
  return [...m.entries()]
    .map(([code, count]) => ({ code, count }))
    .sort((a, b) => a.code.localeCompare(b.code));
}

/**
 * @param {object} props
 * @param {React.ReactNode} [props.children] — רשימת תוצאות (בעמודה הראשית)
 */
export default function FlightResultsFiltersPanel({
  isMobile,
  filter,
  setFilter,
  sortMode,
  setSortMode,
  bounds,
  routeCounts,
  showRouteFilter,
  traitsList,
  drawerOpen,
  setDrawerOpen,
  numShown,
  numTotal,
  onClearFilters,
  airlinesCatalog = [],
  children,
  appearance = "v1",
}) {
  const [desktopAdvancedKeys, setDesktopAdvancedKeys] = useState([]);

  const airlineNameByCode = useMemo(() => {
    const map = {};
    for (const row of airlinesCatalog || []) {
      const code = String(row?.code || "")
        .trim()
        .toUpperCase();
      const name = String(row?.name || "").trim();
      if (!code || !name) continue;
      map[code] = name;
    }
    for (const [code, name] of Object.entries(AIRLINE_NAME_FALLBACK_HE)) {
      if (!map[code]) map[code] = name;
    }
    return map;
  }, [airlinesCatalog]);

  const airlineOptions = useMemo(
    () => buildOptionsWithCounts(traitsList, "airlines"),
    [traitsList]
  );
  const depAirportOptions = useMemo(
    () => buildOptionsWithCounts(traitsList, "depAirports"),
    [traitsList]
  );
  const arrAirportOptions = useMemo(
    () => buildOptionsWithCounts(traitsList, "arrAirports"),
    [traitsList]
  );
  const hasReturnSegment = useMemo(
    () => traitsList.some((t) => t.numSegments >= 2),
    [traitsList]
  );

  const formProps = {
    filter,
    setFilter,
    bounds,
    routeCounts,
    showRouteFilter,
    airlineOptions,
    airlineNameByCode,
    depAirportOptions,
    arrAirportOptions,
    hasReturnSegment,
  };

  const toggleChipDirect = () => {
    setFilter({
      stops: filter.stops === "direct" ? null : "direct",
    });
  };

  const layoutClass = [
    "fr-filters-layout",
    isMobile ? "fr-filters-layout--mobile" : "",
    appearance === "v2" ? "fr-filters-layout--v2" : "",
  ]
    .filter(Boolean)
    .join(" ");

  if (appearance === "v2") {
    const stopsValue = filter.stops ?? "any";
    const priceRange =
      filter.pricePerPersonRange && filter.pricePerPersonRange.length === 2
        ? filter.pricePerPersonRange
        : bounds?.pricePerPerson;
    const priceFiltered = Boolean(
      bounds?.pricePerPerson &&
        filter.pricePerPersonRange &&
        filter.pricePerPersonRange.length === 2 &&
        (filter.pricePerPersonRange[0] !== bounds.pricePerPerson[0] ||
          filter.pricePerPersonRange[1] !== bounds.pricePerPerson[1])
    );
    const advancedOpen = desktopAdvancedKeys.includes("advanced");
    const filtersActive = Boolean(
      (filter.stops != null && filter.stops !== "any") ||
        (Array.isArray(filter.airlines) && filter.airlines.length > 0) ||
        filter.timeOutbound ||
        filter.timeReturn ||
        filter.baggage ||
        priceFiltered ||
        (filter.routeType && filter.routeType !== "all") ||
        (Array.isArray(filter.depAirports) && filter.depAirports.length > 0) ||
        (Array.isArray(filter.arrAirports) && filter.arrAirports.length > 0) ||
        filter.durationRangeMin ||
        filter.totalFareRange ||
        filter.useTotalPriceForRange
    );
    const countLabel =
      numShown === numTotal
        ? `${numTotal} תוצאות`
        : `${numShown} מתוך ${numTotal} תוצאות`;
    return (
      <div className={layoutClass} dir="rtl">
        <aside className="fr-filters-v2-sidebar" aria-label="סינון">
          <div className="fr-filters-v2-quick">
            <Select
              size="small"
              className="fr-filters-v2-select"
              value={stopsValue}
              onChange={(v) => setFilter({ stops: v === "any" ? null : v })}
              options={[
                { value: "any", label: "עצירות" },
                { value: "direct", label: "ישיר" },
                { value: "one", label: "עצירה אחת" },
                { value: "two_plus", label: "2+ עצירות" },
              ]}
            />
            <Select
              allowClear
              size="small"
              mode="multiple"
              maxTagCount={1}
              placeholder="חברות"
              className="fr-filters-v2-select fr-filters-v2-select--airlines"
              value={filter.airlines || []}
              onChange={(v) => setFilter({ airlines: v })}
              options={airlineOptions.map(({ code }) => ({
                value: code,
                label: airlineNameByCode[code]
                  ? `${airlineNameByCode[code]} (${code})`
                  : code,
              }))}
            />
            <Select
              allowClear
              size="small"
              placeholder="שעות יציאה"
              className="fr-filters-v2-select"
              value={filter.timeOutbound}
              onChange={(v) => setFilter({ timeOutbound: v ?? null })}
              options={TIME_OPTIONS}
            />
            {hasReturnSegment ? (
              <Select
                allowClear
                size="small"
                placeholder="שעות חזרה"
                className="fr-filters-v2-select"
                value={filter.timeReturn}
                onChange={(v) => setFilter({ timeReturn: v ?? null })}
                options={TIME_OPTIONS}
              />
            ) : null}
            <Select
              allowClear
              size="small"
              placeholder="כבודה"
              className="fr-filters-v2-select"
              value={filter.baggage}
              onChange={(v) => setFilter({ baggage: v ?? null })}
              options={[
                { value: "personal_only", label: "תיק אישי" },
                { value: "trolley", label: "טרולי" },
                { value: "checked", label: "מזוודה" },
              ]}
            />
            {bounds?.pricePerPerson ? (
              <Popover
                trigger="click"
                placement="bottomRight"
                overlayClassName="fr-filters-v2-price-popover"
                content={
                  <div className="fr-filters-v2-price-pop" dir="rtl">
                    <Slider
                      range
                      min={bounds.pricePerPerson[0]}
                      max={bounds.pricePerPerson[1]}
                      step={1}
                      value={priceRange}
                      onChange={(v) => {
                        if (Array.isArray(v) && v.length === 2) {
                          setFilter({ pricePerPersonRange: v });
                        }
                      }}
                    />
                    <p className="fr-filters-v2-price-pop__range" dir="ltr">
                      {Math.round(priceRange[0])} – {Math.round(priceRange[1])}
                    </p>
                  </div>
                }
              >
                <button
                  type="button"
                  className={
                    priceFiltered
                      ? "fr-filter-chip fr-filter-chip--active"
                      : "fr-filter-chip"
                  }
                >
                  מחיר
                </button>
              </Popover>
            ) : null}
            {!isMobile ? (
              <button
                type="button"
                className={
                  advancedOpen
                    ? "fr-filter-chip fr-filter-chip--active"
                    : "fr-filter-chip"
                }
                onClick={() =>
                  setDesktopAdvancedKeys(advancedOpen ? [] : ["advanced"])
                }
              >
                מתקדם
              </button>
            ) : (
              <Button
                type="default"
                size="small"
                icon={<FilterOutlined />}
                onClick={() => setDrawerOpen(true)}
              >
                סינון
              </Button>
            )}
            {filtersActive ? (
              <button
                type="button"
                className="fr-filters-v2-clear"
                onClick={onClearFilters}
              >
                נקה פילטרים
              </button>
            ) : null}
          </div>
          {!isMobile && advancedOpen ? (
            <div className="fr-filters-v2-advanced-panel">
              <FiltersFormBody {...formProps} compactGrid />
              <div className="fr-filters-actions fr-filters-actions--in-collapse">
                <Button onClick={onClearFilters}>נקה הכל</Button>
              </div>
            </div>
          ) : null}
        </aside>
        <div className="fr-filters-v2-results-col">
          <div className="fr-filters-v2-toolbar">
            <span className="fr-filters-v2-count">{countLabel}</span>
            <div className="fr-filters-v2-sorts" role="group" aria-label="מיון">
              {[
                { value: "recommended", label: "המומלץ" },
                { value: "cheapest", label: "הזול ביותר" },
                { value: "fastest", label: "המהיר ביותר" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={
                    sortMode === opt.value
                      ? "fr-filter-chip fr-filter-chip--active"
                      : "fr-filter-chip"
                  }
                  onClick={() => setSortMode(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="fr-filters-results">{children}</div>
        </div>
        <Drawer
          title="סינון תוצאות"
          placement="bottom"
          height="88%"
          open={isMobile && drawerOpen}
          onClose={() => setDrawerOpen(false)}
          destroyOnHidden={false}
          styles={{ body: { paddingTop: 12 } }}
        >
          <FiltersFormBody {...formProps} />
          <div className="fr-filters-actions">
            <Button type="primary" onClick={() => setDrawerOpen(false)}>
              הצג תוצאות
            </Button>
            <Button onClick={onClearFilters}>נקה הכל</Button>
          </div>
        </Drawer>
      </div>
    );
  }

  return (
    <div className={layoutClass} dir="rtl">
      <div className="fr-filters-main">
        <div className="fr-filters-top-surface">
          <div className="fr-filters-toolbar">
            <div className="fr-filters-toolbar__sort">
              <span className="fr-filters-toolbar__sort-label">מיון</span>
              <Select
                className="fr-filters-toolbar__sort-select"
                popupMatchSelectWidth={false}
                value={sortMode}
                onChange={setSortMode}
                options={SORT_OPTIONS}
                placeholder="בחר מיון"
              />
            </div>
            {isMobile ? (
              <Button
                type="default"
                icon={<FilterOutlined />}
                className="fr-filters-mobile-trigger"
                onClick={() => setDrawerOpen(true)}
              >
                סינון
              </Button>
            ) : null}
            <span className="fr-filters-toolbar__count">
              מוצגים {numShown} מתוך {numTotal}
            </span>
          </div>

          <div className="fr-filters-chips">
            <button
              type="button"
              className={
                filter.stops === "direct"
                  ? "fr-filter-chip fr-filter-chip--active"
                  : "fr-filter-chip"
              }
              onClick={toggleChipDirect}
            >
              ישירה בלבד
            </button>
            <button
              type="button"
              className={
                sortMode === "cheapest"
                  ? "fr-filter-chip fr-filter-chip--active"
                  : "fr-filter-chip"
              }
              onClick={() => setSortMode("cheapest")}
            >
              הכי זול
            </button>
            <button
              type="button"
              className={
                sortMode === "fastest"
                  ? "fr-filter-chip fr-filter-chip--active"
                  : "fr-filter-chip"
              }
              onClick={() => setSortMode("fastest")}
            >
              הכי מהיר
            </button>
            <button
              type="button"
              className={
                sortMode === "fewest_stops"
                  ? "fr-filter-chip fr-filter-chip--active"
                  : "fr-filter-chip"
              }
              onClick={() => setSortMode("fewest_stops")}
            >
              פחות עצירות
            </button>
          </div>

          {!isMobile ? (
            <Collapse
              className="fr-filters-advanced-collapse"
              bordered={false}
              ghost
              expandIconPosition="end"
              activeKey={desktopAdvancedKeys}
              onChange={(key) =>
                setDesktopAdvancedKeys(
                  Array.isArray(key) ? key : key != null ? [key] : []
                )
              }
              items={[
                {
                  key: "advanced",
                  label: (
                    <span className="fr-filters-advanced-collapse__label">
                      <span className="fr-filters-advanced-collapse__title">
                        סינון מתקדם
                      </span>
                      <span className="fr-filters-advanced-collapse__subtitle">
                        סוג מסלול, מחיר, שעות, עצירות, חברות תעופה, כבודה, משך,
                        שדות יציאה/נחיתה
                      </span>
                    </span>
                  ),
                  children: (
                    <>
                      <FiltersFormBody {...formProps} compactGrid />
                      <div className="fr-filters-actions fr-filters-actions--in-collapse">
                        <Button onClick={onClearFilters}>נקה הכל</Button>
                      </div>
                    </>
                  ),
                },
              ]}
            />
          ) : null}
        </div>

        <div className="fr-filters-results">{children}</div>

        <Drawer
          title="סינון תוצאות"
          placement="bottom"
          height="88%"
          open={isMobile && drawerOpen}
          onClose={() => setDrawerOpen(false)}
          destroyOnHidden={false}
          styles={{ body: { paddingTop: 12 } }}
        >
          <FiltersFormBody {...formProps} />
          <div className="fr-filters-actions">
            <Button type="primary" onClick={() => setDrawerOpen(false)}>
              הצג תוצאות
            </Button>
            <Button onClick={onClearFilters}>נקה הכל</Button>
          </div>
        </Drawer>
      </div>
    </div>
  );
}
